import type { Invoice, InvoiceLine, InvoiceStatus, StudioQuoteMetadata } from '../types/invoice.types';
import { supabase } from '@/shared/lib/supabase';

/**
 * Invoice service — reads from Supabase `invoice` + `invoice_line`, RLS-scoped
 * to `customer_id = auth.uid()`. Mutations (mark-paid, cancel) go through Edge
 * Functions wrapped in idempotency_key — those land in slice 2.
 */

interface InvoiceRow {
  id: string;
  number: string;
  customer_id: string;
  issue_date: string;
  due_date: string;
  status: InvoiceStatus;
  subtotal: number | string;
  vat_amount: number | string;
  total: number | string;
  currency: 'ZAR';
  paid_at: string | null;
  cancelled_at: string | null;
  pdf_storage_path: string | null;
  notes: string | null;
  payment_token: string | null;
  doc_type: 'quotation' | 'invoice';
  quote_number: string | null;
  quote_expires_at: string | null;
  quote_metadata: StudioQuoteMetadata | null;
}

interface InvoiceLineRow {
  id: string;
  invoice_id: string;
  description: string;
  target_kind: string;
  target_ref: string | null;
  quantity: number | string;
  unit_price: number | string;
  vat_rate: number | string;
  vat_amount: number | string;
  total: number | string;
  service_plan_id: string | null;
  period_start: string | null;
  period_end: string | null;
  display_order: number;
  discount_amount: number | string | null;
  grant_reason: string | null;
  related_service_id?: string;
  related_domain_id?: string;
}

const INVOICE_COLS =
  'id, number, customer_id, issue_date, due_date, status, subtotal, vat_amount, total, currency, paid_at, cancelled_at, pdf_storage_path, notes, payment_token, doc_type, quote_number, quote_expires_at, quote_metadata';

const LINE_COLS =
  'id, invoice_id, description, target_kind, target_ref, quantity, unit_price, discount_amount, grant_reason, vat_rate, vat_amount, total, service_plan_id, period_start, period_end, display_order';

/** Postgres `numeric` columns come through as strings — coerce safely. */
const num = (v: number | string | null): number => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  return Number(v) || 0;
};

const mapRowToInvoice = (row: InvoiceRow, lines: InvoiceLineRow[]): Invoice => ({
  id: row.id,
  customerId: row.customer_id,
  number: row.number,
  status: row.status,
  issuedAt: row.issue_date,
  dueAt: row.due_date,
  paidAt: row.paid_at ?? undefined,
  subtotalZAR: num(row.subtotal),
  vatZAR: num(row.vat_amount),
  totalZAR: num(row.total),
  currency: 'ZAR',
  lines: lines.map(mapLineRow),
  pdfUrl: row.pdf_storage_path ?? undefined,
  paymentToken: row.payment_token ?? undefined,
  docType: row.doc_type ?? 'invoice',
  quoteNumber: row.quote_number ?? undefined,
  quoteExpiresAt: row.quote_expires_at ?? undefined,
  quoteMetadata: row.quote_metadata ?? null,
});

const mapLineRow = (row: InvoiceLineRow): InvoiceLine => ({
  id: row.id,
  description: row.description,
  quantity: num(row.quantity),
  unitPriceZAR: num(row.unit_price),
  vatRate: num(row.vat_rate),
  amountZAR: num(row.total),
  discountZAR: num(row.discount_amount),
  grantReason: row.grant_reason ?? null,
  relatedServiceId: row.related_service_id,
  relatedDomainId: row.related_domain_id,
});

export const invoiceService = {
  async list(): Promise<Invoice[]> {
    const { data: invoiceRows, error: invoiceError } = await supabase
      .from('invoice')
      .select(INVOICE_COLS)
      .order('issue_date', { ascending: false });

    if (invoiceError) {
      throw new Error(`Could not load invoices: ${invoiceError.message}`);
    }
    if (!invoiceRows || invoiceRows.length === 0) {
      return [];
    }

    const ids = invoiceRows.map((r) => r.id);
    const { data: lineRows, error: lineError } = await supabase
      .from('invoice_line')
      .select(LINE_COLS)
      .in('invoice_id', ids)
      .order('display_order', { ascending: true });

    if (lineError) {
      throw new Error(`Could not load invoice lines: ${lineError.message}`);
    }

    const linesByInvoice = new Map<string, InvoiceLineRow[]>();
    for (const line of lineRows ?? []) {
      const list = linesByInvoice.get(line.invoice_id) ?? [];
      list.push(line as InvoiceLineRow);
      linesByInvoice.set(line.invoice_id, list);
    }

    return invoiceRows.map((row) =>
      mapRowToInvoice(row as InvoiceRow, linesByInvoice.get(row.id) ?? [])
    );
  },

  async getById(id: string): Promise<Invoice | null> {
    const { data: invoiceRow, error: invoiceError } = await supabase
      .from('invoice')
      .select(INVOICE_COLS)
      .eq('id', id)
      .maybeSingle();

    if (invoiceError) {
      throw new Error(`Could not load invoice: ${invoiceError.message}`);
    }
    if (!invoiceRow) return null;

    const { data: lineRows, error: lineError } = await supabase
      .from('invoice_line')
      .select(LINE_COLS)
      .eq('invoice_id', id)
      .order('display_order', { ascending: true });

    if (lineError) {
      throw new Error(`Could not load invoice lines: ${lineError.message}`);
    }

    return mapRowToInvoice(invoiceRow as InvoiceRow, (lineRows ?? []) as InvoiceLineRow[]);
  },
};
